import {
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { Alert, Button, Progress, Space, Typography } from 'antd'
import { DraftPdfGeneration } from 'pages/reporting/reports/rug/hooks/UseDraftPdfGeneration'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React from 'react'
import styled from 'styled-components'

const { Text } = Typography

const GeneratingStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.25rem;
  text-align: center;
`

const GeneratingIcon = styled(LoadingOutlined)`
  font-size: 1.5rem;
`

interface DraftPdfControlsProps {
  generation: DraftPdfGeneration
}

/** Generate, preview, and download the draft PDF that reviewers sign off on. */
export const DraftPdfControls: React.FC<DraftPdfControlsProps> = ({
  generation,
}) => {
  const { pdfState, isSubmitting, assetPicturesLoading } = useRugReportOps()
  const {
    isGenerating,
    progress,
    currentStep,
    generateDraft,
    downloadCurrentDraft,
    previewCurrentDraft,
  } = generation

  return (
    <Space orientation='vertical' size='small' style={{ width: '100%' }}>
      <Text type='secondary'>
        Generate a draft of the report for review. Reviewers sign this draft
        before it is published.
      </Text>
      {assetPicturesLoading && (
        <Alert
          title='Preparing images for PDF generation'
          description='Selected report images are being converted into PDF-safe data URLs. PDF generation will be enabled when this finishes.'
          type='info'
          showIcon
        />
      )}
      {isGenerating ? (
        <GeneratingStatus>
          <GeneratingIcon />
          <Progress percent={progress} status='active' />
          <Text>{currentStep}</Text>
        </GeneratingStatus>
      ) : (
        <Space wrap>
          {pdfState.latestDraftTimestamp && (
            <Button
              type='primary'
              icon={<EyeOutlined />}
              onClick={previewCurrentDraft}>
              Preview Latest Draft
            </Button>
          )}
          <Button
            type={pdfState.latestDraftTimestamp ? 'default' : 'primary'}
            icon={<SaveOutlined />}
            onClick={() => generateDraft()}
            loading={isGenerating}
            disabled={isGenerating || assetPicturesLoading}>
            {pdfState.latestDraftTimestamp ? 'Regenerate PDF' : 'Generate PDF'}
          </Button>
          {pdfState.latestDraftTimestamp && (
            <>
              <Button
                icon={<FileTextOutlined />}
                onClick={downloadCurrentDraft}
                loading={isSubmitting}>
                Download Latest Draft
              </Button>
              <Text type='secondary'>
                Last generated: {pdfState.latestDraftTimestamp}
              </Text>
            </>
          )}
        </Space>
      )}
    </Space>
  )
}
